<template>
  <modal :show.sync="show" :on-close="onClose">
    <!-- Etapa 1: upload -->
    <div v-if="step === 'upload'" class="column content-box">
      <woot-modal-header
        :header-title="$t('CRM_BULK_IMPORT.TITLE')"
        :header-content="$t('CRM_BULK_IMPORT.DESC')"
      />
      <div class="modal-body">
        <div class="form-row">
          <label>{{ $t('CRM_BULK_IMPORT.FUNNEL.LABEL') }}</label>
          <select v-model="funnelId" class="select-input">
            <option :value="null" disabled>
              {{ $t('CRM_BULK_IMPORT.FUNNEL.PLACEHOLDER') }}
            </option>
            <option
              v-for="funnel in funnelOptions"
              :key="funnel.id"
              :value="funnel.id"
            >
              {{ funnel.name }}
            </option>
          </select>
        </div>

        <div class="model-box">
          <div class="model-box-info">
            <span class="model-box-title">{{
              $t('CRM_BULK_IMPORT.MODEL.TITLE')
            }}</span>
            <span class="model-box-desc">{{
              $t('CRM_BULK_IMPORT.MODEL.DESC')
            }}</span>
          </div>
          <a :href="modelUrl" download class="model-box-link">
            {{ $t('CRM_BULK_IMPORT.MODEL.DOWNLOAD') }}
          </a>
        </div>

        <label
          class="dropzone"
          :class="{ 'dropzone--error': fileError, 'dropzone--active': isDragging }"
          @dragover.prevent="isDragging = true"
          @dragenter.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleFileDrop"
        >
          <input
            ref="file"
            type="file"
            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .xlsx, .xls"
            class="dropzone-input"
            @change="handleFileUpload"
          />
          <fluent-icon icon="folder" size="32" class="dropzone-icon" />
          <p class="dropzone-text">
            <template v-if="file">{{ file.name }}</template>
            <template v-else>{{ $t('CRM_BULK_IMPORT.UPLOAD.HINT') }}</template>
          </p>
          <span class="dropzone-formats">{{
            $t('CRM_BULK_IMPORT.UPLOAD.FORMATS')
          }}</span>
        </label>
        <p v-if="fileError" class="error-text">{{ fileError }}</p>

        <div class="modal-footer">
          <button class="button clear" @click.prevent="onClose">
            {{ $t('CRM_BULK_IMPORT.ACTIONS.CANCEL') }}
          </button>
          <woot-button :disabled="!file" @click="startProcessing">
            {{ $t('CRM_BULK_IMPORT.ACTIONS.SEND') }}
          </woot-button>
        </div>
      </div>
    </div>

    <!-- Etapa 2: processando -->
    <div v-else-if="step === 'processing'" class="processing-content">
      <p class="processing-message">
        {{ $t('CRM_BULK_IMPORT.PROCESSING') }} {{ progress }}%
      </p>
      <div class="progress-track">
        <div class="progress-bar" :style="{ width: `${progress}%` }" />
      </div>
    </div>

    <!-- Etapa 3: mapeamento -->
    <div v-else-if="step === 'mapping'" class="column content-box">
      <woot-modal-header :header-title="$t('CRM_BULK_IMPORT.MAPPING.TITLE')" />
      <div class="modal-body mapping-body">
        <div class="map-row">
          <label class="map-label">
            {{ $t('CRM_BULK_IMPORT.MAPPING.FIELDS.TITLE') }}
            <span class="required">*</span>
          </label>
          <select v-model="mapping.title" class="select-input">
            <option value="">{{ columnPlaceholder }}</option>
            <option v-for="col in headers" :key="col" :value="col">
              {{ col }}
            </option>
          </select>
        </div>

        <div
          v-for="field in contactFields"
          :key="field.key"
          class="map-row"
        >
          <label class="map-label">{{ field.label }}</label>
          <select v-model="mapping[field.key]" class="select-input">
            <option value="">{{ columnPlaceholder }}</option>
            <option v-for="col in headers" :key="col" :value="col">
              {{ col }}
            </option>
          </select>
        </div>

        <div class="map-row">
          <label class="map-label">{{
            $t('CRM_BULK_IMPORT.MAPPING.FIELDS.LABELS')
          }}</label>
          <select v-model="mapping.labels" class="select-input">
            <option value="">{{ columnPlaceholder }}</option>
            <option v-for="col in headers" :key="col" :value="col">
              {{ col }}
            </option>
          </select>
        </div>

        <div class="map-row">
          <label class="map-label">{{
            $t('CRM_BULK_IMPORT.MAPPING.FIELDS.STAGE')
          }}</label>
          <select v-model="columnsId" class="select-input">
            <option :value="null" disabled>{{ columnPlaceholder }}</option>
            <option v-for="col in stageOptions" :key="col.id" :value="col.id">
              {{ col.name }}
            </option>
          </select>
        </div>

        <div class="map-row">
          <label class="map-label">{{
            $t('CRM_BULK_IMPORT.MAPPING.FIELDS.PRIORITY')
          }}</label>
          <select v-model="priority" class="select-input">
            <option v-for="opt in priorityOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </div>

        <div v-if="showSaleValue" class="map-row">
          <label class="map-label">{{
            $t('CRM_BULK_IMPORT.MAPPING.FIELDS.SALE_VALUE')
          }}</label>
          <select v-model="mapping.sale_value" class="select-input">
            <option value="">{{ columnPlaceholder }}</option>
            <option v-for="col in headers" :key="col" :value="col">
              {{ col }}
            </option>
          </select>
        </div>

        <!-- Contatos alternativos -->
        <h4 class="section-title">
          {{ $t('CRM_BULK_IMPORT.ALT_CONTACTS.TITLE') }}
        </h4>
        <div
          v-for="(alt, index) in altContacts"
          :key="`alt-${index}`"
          class="alt-contact"
        >
          <div class="map-row">
            <label class="map-label">{{
              $t('CRM_BULK_IMPORT.ALT_CONTACTS.NAME')
            }}</label>
            <select v-model="alt.name" class="select-input">
              <option value="">{{ columnPlaceholder }}</option>
              <option v-for="col in headers" :key="col" :value="col">
                {{ col }}
              </option>
            </select>
          </div>
          <div class="map-row">
            <label class="map-label">{{
              $t('CRM_BULK_IMPORT.ALT_CONTACTS.PHONE')
            }}</label>
            <select v-model="alt.phone" class="select-input">
              <option value="">{{ columnPlaceholder }}</option>
              <option v-for="col in headers" :key="col" :value="col">
                {{ col }}
              </option>
            </select>
          </div>
          <div class="map-row">
            <label class="map-label">{{
              $t('CRM_BULK_IMPORT.ALT_CONTACTS.EMAIL')
            }}</label>
            <select v-model="alt.email" class="select-input">
              <option value="">{{ columnPlaceholder }}</option>
              <option v-for="col in headers" :key="col" :value="col">
                {{ col }}
              </option>
            </select>
          </div>
          <button class="link-remove" @click.prevent="removeAltContact(index)">
            {{ $t('CRM_BULK_IMPORT.ACTIONS.REMOVE') }}
          </button>
        </div>
        <button
          class="add-button"
          :disabled="!hasMainContact"
          @click.prevent="addAltContact"
        >
          {{ $t('CRM_BULK_IMPORT.ALT_CONTACTS.ADD') }}
        </button>

        <!-- Atributos dinâmicos -->
        <h4 class="section-title">
          {{ $t('CRM_BULK_IMPORT.ATTRIBUTES.TITLE') }}
        </h4>
        <div
          v-for="(attr, index) in attributes"
          :key="`attr-${index}`"
          class="attribute-row"
        >
          <input
            v-model="attr.name"
            class="attribute-name"
            :placeholder="$t('CRM_BULK_IMPORT.ATTRIBUTES.NAME_PLACEHOLDER')"
          />
          <select v-model="attr.column" class="select-input">
            <option value="">{{ columnPlaceholder }}</option>
            <option v-for="col in headers" :key="col" :value="col">
              {{ col }}
            </option>
          </select>
          <button class="link-remove" @click.prevent="removeAttribute(index)">
            {{ $t('CRM_BULK_IMPORT.ACTIONS.REMOVE') }}
          </button>
        </div>
        <button
          class="add-button"
          :disabled="attributes.length >= maxAttributes"
          @click.prevent="addAttribute"
        >
          {{ $t('CRM_BULK_IMPORT.ATTRIBUTES.ADD') }}
        </button>

        <div class="modal-footer">
          <button class="button clear" @click.prevent="onClose">
            {{ $t('CRM_BULK_IMPORT.ACTIONS.CANCEL') }}
          </button>
          <woot-button
            :disabled="!mapping.title || isSubmitting"
            :loading="isSubmitting"
            @click="submitImport"
          >
            {{ $t('CRM_BULK_IMPORT.ACTIONS.CREATE') }}
          </woot-button>
        </div>
      </div>
    </div>

    <!-- Etapa 4: resumo -->
    <div v-else-if="step === 'summary'" class="summary-container">
      <h3 class="summary-title">{{ $t('CRM_BULK_IMPORT.SUMMARY.TITLE') }}</h3>
      <div class="summary-stats">
        <div class="summary-stat summary-stat--success">
          <span class="summary-stat-value">{{ summary.created_records || 0 }}</span>
          <span class="summary-stat-label">{{
            $t('CRM_BULK_IMPORT.SUMMARY.CREATED')
          }}</span>
        </div>
        <div class="summary-stat summary-stat--danger">
          <span class="summary-stat-value">{{ summary.error_records || 0 }}</span>
          <span class="summary-stat-label">{{
            $t('CRM_BULK_IMPORT.SUMMARY.ERRORS')
          }}</span>
        </div>
      </div>
      <div class="summary-actions">
        <a
          v-if="summary.error_records > 0 && summary.failed_records_url"
          :href="summary.failed_records_url"
          target="_blank"
          class="summary-download-link"
        >
          {{ $t('CRM_BULK_IMPORT.SUMMARY.DOWNLOAD_ERRORS') }}
        </a>
        <woot-button @click="onSummaryClose">
          {{ $t('CRM_BULK_IMPORT.ACTIONS.CLOSE') }}
        </woot-button>
      </div>
    </div>
  </modal>
</template>

<script>
import { mapGetters } from 'vuex';
import XLSX from 'xlsx';
import alertMixin from 'shared/mixins/alertMixin';
import Modal from '../../../../components/Modal';
import TicketImportAPI from '../../../../api/ticketImport';
import { canAccessCRM } from '../../../../helper/crmPermissionHelper';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 200;

export default {
  components: { Modal },
  mixins: [alertMixin],
  props: {
    preselectedKanban: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      show: true,
      step: 'upload',
      file: null,
      fileError: '',
      isDragging: false,
      headers: [],
      progress: 0,
      progressTimer: null,
      pollingTimer: null,
      isSubmitting: false,
      funnelId: this.preselectedKanban ? this.preselectedKanban.id : null,
      columnsId: null,
      priority: 'medium',
      maxAttributes: 15,
      mapping: {
        title: '',
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        labels: '',
        sale_value: '',
      },
      altContacts: [],
      attributes: [],
      summary: {},
    };
  },
  computed: {
    ...mapGetters({
      kanbanList: 'getKanbanfunnels',
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
      getAccount: 'accounts/getAccount',
    }),
    account() {
      return this.getAccount(this.currentAccountId);
    },
    funnelOptions() {
      return (this.kanbanList || []).filter(
        funnel =>
          funnel.active &&
          canAccessCRM(this.account, this.currentUser, [funnel])
      );
    },
    selectedFunnel() {
      return this.funnelOptions.find(funnel => funnel.id === this.funnelId);
    },
    stageOptions() {
      return this.selectedFunnel ? this.selectedFunnel.columns || [] : [];
    },
    showSaleValue() {
      return (
        !!this.selectedFunnel &&
        this.selectedFunnel.type_opportunity !== 'tickets'
      );
    },
    columnPlaceholder() {
      return this.$t('CRM_BULK_IMPORT.MAPPING.COLUMN_PLACEHOLDER');
    },
    contactFields() {
      return [
        {
          key: 'contact_name',
          label: this.$t('CRM_BULK_IMPORT.MAPPING.FIELDS.CONTACT_NAME'),
        },
        {
          key: 'contact_phone',
          label: this.$t('CRM_BULK_IMPORT.MAPPING.FIELDS.CONTACT_PHONE'),
        },
        {
          key: 'contact_email',
          label: this.$t('CRM_BULK_IMPORT.MAPPING.FIELDS.CONTACT_EMAIL'),
        },
      ];
    },
    priorityOptions() {
      return [
        {
          value: 'critical',
          label: this.$t('TICKET.FORM.PRIORITY.OPTIONS.CRITICAL'),
        },
        { value: 'high', label: this.$t('TICKET.FORM.PRIORITY.OPTIONS.HIGH') },
        {
          value: 'medium',
          label: this.$t('TICKET.FORM.PRIORITY.OPTIONS.MEDIUM'),
        },
        { value: 'low', label: this.$t('TICKET.FORM.PRIORITY.OPTIONS.LOW') },
      ];
    },
    hasMainContact() {
      return (
        !!this.mapping.contact_name &&
        (!!this.mapping.contact_phone || !!this.mapping.contact_email)
      );
    },
    modelUrl() {
      return '/downloads/import-tickets-xlsx-sample.xlsx';
    },
  },
  beforeDestroy() {
    this.stopPolling();
    this.stopProgress();
  },
  methods: {
    onClose() {
      this.stopPolling();
      this.stopProgress();
      this.$emit('cancel');
    },
    onSummaryClose() {
      this.$emit('success');
    },
    handleFileUpload(event) {
      this.setFile(event.target.files[0]);
    },
    handleFileDrop(event) {
      this.isDragging = false;
      const dropped = event.dataTransfer.files && event.dataTransfer.files[0];
      this.setFile(dropped);
    },
    setFile(uploaded) {
      this.fileError = '';
      if (!uploaded) return;

      const ext = uploaded.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls'].includes(ext)) {
        this.file = null;
        this.fileError = this.$t('CRM_BULK_IMPORT.UPLOAD.INVALID_FORMAT');
        return;
      }
      this.file = uploaded;
    },
    startProcessing() {
      if (!this.file) {
        this.fileError = this.$t('CRM_BULK_IMPORT.UPLOAD.REQUIRED');
        return;
      }
      this.step = 'processing';
      this.progress = 0;
      this.runProgress();
      this.parseHeaders();
    },
    runProgress() {
      this.progressTimer = setInterval(() => {
        if (this.progress < 90) {
          this.progress += 10;
        }
      }, 120);
    },
    stopProgress() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
    },
    parseHeaders() {
      const reader = new FileReader();
      reader.onload = e => {
        try {
          const workbook = XLSX.read(e.target.result, { type: 'binary' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          this.headers = (rows[0] || [])
            .map(h => String(h).trim())
            .filter(Boolean);

          if (!this.headers.length) {
            this.failProcessing(this.$t('CRM_BULK_IMPORT.UPLOAD.EMPTY_FILE'));
            return;
          }
          this.finishProcessing();
        } catch (error) {
          this.failProcessing(this.$t('CRM_BULK_IMPORT.UPLOAD.PARSE_ERROR'));
        }
      };
      reader.onerror = () => {
        this.failProcessing(this.$t('CRM_BULK_IMPORT.UPLOAD.PARSE_ERROR'));
      };
      reader.readAsBinaryString(this.file);
    },
    finishProcessing() {
      this.stopProgress();
      this.progress = 100;
      this.columnsId = this.stageOptions[0] ? this.stageOptions[0].id : null;
      setTimeout(() => {
        this.step = 'mapping';
      }, 300);
    },
    failProcessing(message) {
      this.stopProgress();
      this.showAlert(message);
      this.step = 'upload';
    },
    addAltContact() {
      if (!this.hasMainContact) return;
      this.altContacts.push({ name: '', phone: '', email: '' });
    },
    removeAltContact(index) {
      this.altContacts.splice(index, 1);
    },
    addAttribute() {
      if (this.attributes.length >= this.maxAttributes) return;
      this.attributes.push({ name: '', column: '' });
    },
    removeAttribute(index) {
      this.attributes.splice(index, 1);
    },
    buildMappingPayload() {
      return {
        title: this.mapping.title,
        contact_name: this.mapping.contact_name,
        contact_phone: this.mapping.contact_phone,
        contact_email: this.mapping.contact_email,
        labels: this.mapping.labels,
        sale_value: this.showSaleValue ? this.mapping.sale_value : '',
        alt_contacts: this.altContacts.filter(c => c.name),
        attributes: this.attributes.filter(a => a.name && a.column),
      };
    },
    async submitImport() {
      if (!this.mapping.title) {
        this.showAlert(this.$t('CRM_BULK_IMPORT.MAPPING.TITLE_REQUIRED'));
        return;
      }
      this.isSubmitting = true;
      try {
        const response = await TicketImportAPI.create({
          file: this.file,
          kanbanfunnelId: this.funnelId,
          columnsId: this.columnsId,
          priority: this.priority,
          mapping: this.buildMappingPayload(),
          userId: this.currentUser.id,
        });
        await this.pollStatus(response.data.id);
      } catch (error) {
        this.isSubmitting = false;
        this.showAlert(this.$t('CRM_BULK_IMPORT.SUMMARY.ERROR_MESSAGE'));
      }
    },
    pollStatus(importId) {
      return new Promise((resolve, reject) => {
        let attempts = 0;
        this.pollingTimer = setInterval(async () => {
          attempts += 1;
          if (attempts > MAX_POLL_ATTEMPTS) {
            this.stopPolling();
            this.isSubmitting = false;
            reject(new Error('timeout'));
            return;
          }
          try {
            const { data } = await TicketImportAPI.getStatus(importId);
            if (data.status === 'completed' || data.status === 'failed') {
              this.stopPolling();
              this.isSubmitting = false;
              this.summary = data;
              this.step = 'summary';
              resolve(data);
            }
          } catch (error) {
            this.stopPolling();
            this.isSubmitting = false;
            reject(error);
          }
        }, POLL_INTERVAL_MS);
      });
    },
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-body {
  padding: var(--space-normal) var(--space-large) var(--space-large);
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: var(--space-smaller);
  margin-bottom: var(--space-normal);
}

.select-input {
  margin: 0;
  height: 3.6rem;
  border-radius: var(--border-radius-normal);
  border: 1px solid var(--color-border);
  background-color: var(--white);
}

.model-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-small) var(--space-normal);
  background: var(--s-50);
  border-radius: var(--border-radius-normal);
  margin-bottom: var(--space-normal);
}

.model-box-info {
  display: flex;
  flex-direction: column;
}

.model-box-title {
  font-weight: 600;
}

.model-box-desc {
  font-size: var(--font-size-mini);
  color: var(--s-600);
}

.model-box-link {
  font-weight: 600;
  white-space: nowrap;
}

.dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-smaller);
  padding: var(--space-large);
  border: 1px dashed var(--w-400);
  border-radius: var(--border-radius-normal);
  cursor: pointer;
  text-align: center;

  &--error {
    border-color: var(--r-400);
  }

  &--active {
    border-color: var(--w-500);
    background: var(--w-50);
  }
}

.dropzone-input {
  display: none;
}

.dropzone-icon {
  color: var(--s-500);
}

.dropzone-text {
  margin: 0;
  font-weight: 500;
}

.dropzone-formats {
  font-size: var(--font-size-mini);
  color: var(--s-500);
}

.error-text {
  color: var(--r-500);
  font-size: var(--font-size-small);
  margin-top: var(--space-smaller);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-small);
  margin-top: var(--space-large);
}

.processing-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-normal);
  padding: var(--space-larger) var(--space-large);
}

.processing-message {
  font-weight: 600;
  margin: 0;
}

.progress-track {
  width: 100%;
  height: 1rem;
  background: var(--s-100);
  border-radius: var(--border-radius-large);
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: rgb(40, 167, 69);
  transition: width 0.15s ease;
}

.mapping-body {
  max-height: 60vh;
  overflow-y: auto;
}

.map-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: var(--space-normal);
  margin-bottom: var(--space-small);
}

.map-label {
  font-size: var(--font-size-small);
  margin: 0;
}

.required {
  color: var(--r-500);
}

.section-title {
  font-size: var(--font-size-medium);
  font-weight: 600;
  margin: var(--space-normal) 0 var(--space-small);
}

.alt-contact {
  padding: var(--space-small);
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-normal);
  margin-bottom: var(--space-small);
}

.attribute-row {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  align-items: center;
  gap: var(--space-small);
  margin-bottom: var(--space-small);
}

.attribute-name {
  margin: 0;
  height: 3.6rem;
  font-size: var(--font-size-default);
  border-radius: var(--border-radius-normal);
  border: 1px solid var(--color-border);
}

.add-button {
  background: var(--w-75);
  color: var(--w-700);
  border-radius: var(--border-radius-normal);
  padding: var(--space-small) var(--space-normal);
  font-weight: 600;
  font-size: var(--font-size-small);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.link-remove {
  color: var(--r-500);
  font-size: var(--font-size-mini);
  background: transparent;
}

.summary-container {
  padding: var(--space-large);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-normal);
}

.summary-title {
  font-weight: 600;
  margin: 0;
}

.summary-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-normal);
  width: 100%;
}

.summary-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-normal);
  border-radius: var(--border-radius-normal);
  background: var(--s-50);
  border: 1px solid var(--s-100);
}

.summary-stat-value {
  font-size: 2.4rem;
  font-weight: 700;
}

.summary-stat-label {
  font-size: var(--font-size-mini);
  color: var(--s-600);
}

.summary-stat--success .summary-stat-value {
  color: var(--g-600);
}

.summary-stat--danger .summary-stat-value {
  color: var(--r-600);
}

.summary-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-small);
}

.summary-download-link {
  font-weight: 600;
  font-size: var(--font-size-small);
}
</style>
